import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TransactionModel, TransactionWrapper } from '@core/models';
import { ApiException } from '@core/services/base-connection.service';
import { ANALYTICS_EVENTS, AnalyticsService } from '@core/services/analytics';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ConnectionService } from '../../services/connection.service';

const INVALID_DISCOUNT_CODE_HINT = 'This discount code is invalid';

@Component({
  selector: 'app-discount-code',
  imports: [FormsModule, TooltipModule],
  templateUrl: './discount-code.component.html',
  styleUrl: './discount-code.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscountCodeComponent {
  private connectionService = inject(ConnectionService);
  private analyticsService = inject(AnalyticsService);

  transaction = input.required<TransactionWrapper>();
  // True while the currently selected upsell's buyer type isn't editable in ProVenue: applying
  // a discount code re-prices every ticket in the seats offer via editTicketPrice, which
  // ProVenue rejects outright if any of those tickets sit under a non-editable buyer type. Set
  // by the parent from the mirror condition to GroupOutingComponent.isUpsellDisabled.
  disabled = input<boolean>(false);
  transactionChange = output<TransactionWrapper>();

  code = signal('');
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  private lastTransactionId: number | string | null = null;

  constructor() {
    // This component instance can outlive a single transaction (a cancel/restart/
    // expiry flow deletes the current transaction and creates a new one without necessarily
    // destroying this component). Without this, a stale typed code or a leftover error message
    // from the old transaction would keep showing on the new one.
    effect(() => {
      const currentTransactionId = this.transaction()?.transaction?.id ?? null;
      if (currentTransactionId !== this.lastTransactionId) {
        this.lastTransactionId = currentTransactionId;
        this.code.set('');
        this.errorMessage.set(null);
      }
    });
  }

  get hasDiscount(): boolean {
    return !!this.transaction()?.transaction?.discountCode;
  }

  get appliedCode(): string | null {
    return this.transaction()?.transaction?.discountCode ?? null;
  }

  apply() {
    if (this.disabled()) {
      return;
    }

    const code = this.code().trim();
    if (!code) {
      return;
    }

    const transaction = this.transaction();
    if (!transaction) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const transactionId = transaction.transaction.id.toString();

    this.connectionService.applyDiscountCode(transactionId, code).subscribe({
      next: result => {
        this.isLoading.set(false);
        this.code.set('');

        if (result.data) {
          const updatedTransaction: TransactionWrapper = {
            ...transaction,
            transaction: result.data as TransactionModel,
          };
          this.transactionChange.emit(updatedTransaction);

          const discountAmount =
            (result.data as TransactionModel).tdcTransaction?.totalDiscount ??
            0;
          this.analyticsService.trackDiscountApplied({
            discountAmount,
            transactionId,
            action: 'applied',
          });
        }
      },
      error: error => {
        this.isLoading.set(false);
        const message = this.resolveErrorMessage(
          error,
          'An error occurred while applying the discount code.'
        );
        this.errorMessage.set(message);

        this.analyticsService.trackError({
          eventName: ANALYTICS_EVENTS.CHECKOUT_VALIDATION_ERROR,
          errorType: 'discount_code_rejected',
          // The backend rejection message echoes the typed code verbatim
          // private/group-link codes must never
          // reach GA4 (same rule as DISCOUNT_APPLIED), so scrub it before reporting.
          errorMessage: this.sanitizeForAnalytics(message, code),
          transactionId,
        });
      },
    });
  }

  remove() {
    const transaction = this.transaction();
    if (!transaction) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const transactionId = transaction.transaction.id.toString();
    // Captured before the removal call — the amount that WAS applied, mirroring how
    // onRainoutChange() reports previousInsurancePrice on its removal path.
    const previousDiscountAmount =
      transaction.transaction.tdcTransaction?.totalDiscount ?? 0;
    const appliedCode = this.appliedCode ?? '';

    this.connectionService.removeDiscountCode(transactionId).subscribe({
      next: result => {
        this.isLoading.set(false);

        if (result.data) {
          const updatedTransaction: TransactionWrapper = {
            ...transaction,
            transaction: result.data as TransactionModel,
          };
          this.transactionChange.emit(updatedTransaction);
          this.analyticsService.trackDiscountApplied({
            discountAmount: previousDiscountAmount,
            transactionId,
            action: 'removed',
          });
        }
      },
      error: error => {
        this.isLoading.set(false);
        const message = this.resolveErrorMessage(
          error,
          'An error occurred while removing the discount code.'
        );
        this.errorMessage.set(message);

        // The failed-removal branch used to be analytics-silent, leaving the 'removed'
        // side of discount_applied without any failure signal at all.
        this.analyticsService.trackError({
          eventName: ANALYTICS_EVENTS.CHECKOUT_ERROR,
          errorType: 'discount_remove_failed',
          errorMessage: this.sanitizeForAnalytics(message, appliedCode),
          transactionId,
        });
      },
    });
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (!(error instanceof ApiException)) {
      return fallback;
    }

    const message: string = error.message ?? '';
    if (message.startsWith(INVALID_DISCOUNT_CODE_HINT)) {
      return INVALID_DISCOUNT_CODE_HINT;
    }

    return message;
  }

  /**
   * Last line of scrubbing a discount code out of a message before it goes to GA4: the
   * backend echoes the code into some rejections ("Discount Code Not Found: NOPE123").
   * If the code appears anywhere in the message,
   * the whole message is replaced by the generic hint rather than trying to cut it out.
   */
  private sanitizeForAnalytics(message: string, code: string): string {
    if (!code) {
      return message;
    }
    return message.toLowerCase().includes(code.toLowerCase())
      ? INVALID_DISCOUNT_CODE_HINT
      : message;
  }
}
